import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, FlaskConical, Plus, Search } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { depuis, pluriel } from '../../lib/format.ts';
import { Badge, Chargement, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau } from '../../components/ui.tsx';
import { BoutonChargerDemo } from '../../components/BoutonChargerDemo.tsx';

export interface SiteResume {
  id: string; name: string; address: string | null; city: string | null; usage: string | null; demo: boolean; updated_at: string;
  client_name: string | null; nb_equipements: number; nb_interventions_ouvertes: number;
}

export const USAGES: Record<string, string> = { tertiaire: 'Tertiaire', residentiel: 'Résidentiel', mixte: 'Mixte', industriel: 'Industriel', autre: 'Autre' };

export function PageSites() {
  const { peut } = useSession();
  const [filtre, setFiltre] = useState('');
  const q = useQuery({ queryKey: ['sites'], queryFn: () => api.get<SiteResume[]>('/api/sites') });
  const liste = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return (q.data ?? []).filter((s) => !f || [s.name, s.city, s.client_name, s.address].some((v) => v?.toLowerCase().includes(f)));
  }, [q.data, filtre]);

  return (
    <div className="pile-l">
      <EntetePage titre="Sites et équipements" sousTitre="Clients, sites, bâtiments, étages, zones, systèmes et équipements installés."
        actions={peut('site.ecrire') && <LienBouton to="/sites/nouveau" variante="principal" icone={<Plus aria-hidden />}>Nouveau site</LienBouton>} />
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && q.data.length === 0 && (
        <Panneau>
          <EtatVide icone={<Building2 />} titre="Aucun site accessible"
            texte="Créez le premier site, ou demandez à l’administrateur de vous donner accès aux sites sur lesquels vous intervenez."
            action={<div className="groupe-btn">{peut('site.ecrire') && <LienBouton to="/sites/nouveau" variante="principal">Créer un site</LienBouton>}<BoutonChargerDemo /></div>} />
        </Panneau>
      )}
      {q.data && q.data.length > 0 && (
        <Panneau serre titre={pluriel(liste.length, 'site')} actions={
          <label className="rangee" style={{ gap: 6 }}>
            <Search size={18} aria-hidden className="discret" />
            <span className="visuellement-cache">Filtrer les sites</span>
            <input className="saisie" style={{ width: 240, maxWidth: '60vw' }} placeholder="Filtrer par nom, ville, client" value={filtre} onChange={(e) => setFiltre(e.target.value)} />
          </label>}>
          <div className="liste">
            {liste.map((s) => (
              <Link key={s.id} to={`/sites/${s.id}`} className="liste-element">
                <Building2 aria-hidden className="discret" />
                <span className="liste-element-corps">
                  <span className="liste-element-titre">{s.name}</span>
                  <span className="petit discret">{[s.client_name, s.city, s.usage && USAGES[s.usage]].filter(Boolean).join(' — ') || 'Client et adresse non renseignés'}</span>
                </span>
                <span className="petit secondaire masque-etroit chiffre">{pluriel(s.nb_equipements, 'équipement')}</span>
                {s.nb_interventions_ouvertes > 0 && <Badge ton="info">{pluriel(s.nb_interventions_ouvertes, 'intervention ouverte', 'interventions ouvertes')}</Badge>}
                {s.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                <span className="tres-petit discret expert-seulement masque-etroit">{depuis(s.updated_at)}</span>
              </Link>
            ))}
            {liste.length === 0 && <EtatVide titre="Aucun site ne correspond" texte="Modifiez le filtre." />}
          </div>
        </Panneau>
      )}
    </div>
  );
}
